<template>
  <div class="billingSwitch">
    <div ref="switcher" class="switcher" role="radiogroup" aria-label="Billing period">
      <span
        v-show="thumbStyle"
        class="switcherThumb"
        :class="{ animated: thumbAnimated }"
        :style="thumbStyle"
        aria-hidden="true"
      />
      <button
        ref="yearlyOption"
        type="button"
        role="radio"
        class="switcherOption"
        :class="{ selected: subscriptionType === 'yearly' }"
        :aria-checked="subscriptionType === 'yearly'"
        @click="subscriptionType = 'yearly'"
      >
        Annual
        <span class="saveBadge">Save 17%</span>
      </button>
      <button
        ref="monthlyOption"
        type="button"
        role="radio"
        class="switcherOption"
        :class="{ selected: subscriptionType === 'monthly' }"
        :aria-checked="subscriptionType === 'monthly'"
        @click="subscriptionType = 'monthly'"
      >
        Monthly
      </button>
    </div>
    <p class="switcherNote" :class="{ hidden: subscriptionType !== 'yearly' }">
      Pay for 10 months, get 12 months.
    </p>
  </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const subscriptionType = defineModel({ type: String, required: true })

const switcher = ref(null)
const yearlyOption = ref(null)
const monthlyOption = ref(null)
const thumbStyle = ref(null)
const thumbAnimated = ref(false)
let switcherObserver = null

const moveThumb = () => {
  const option = subscriptionType.value === 'yearly' ? yearlyOption.value : monthlyOption.value

  if (!option) {
    return
  }

  thumbStyle.value = {
    width: `${option.offsetWidth}px`,
    height: `${option.offsetHeight}px`,
    transform: `translate(${option.offsetLeft}px, ${option.offsetTop}px)`
  }
}

const enableThumbAnimation = () => {
  thumbAnimated.value = true
}

const scheduleThumbAnimation = () => {
  requestAnimationFrame(enableThumbAnimation)
}

const onSubscriptionTypeChange = () => {
  nextTick(moveThumb)
}

watch(subscriptionType, onSubscriptionTypeChange)

onMounted(() => {
  moveThumb()
  // The option widths change once the web font loads and when the bold label switches sides.
  switcherObserver = new ResizeObserver(moveThumb)
  switcherObserver.observe(switcher.value)
  nextTick(scheduleThumbAnimation)
})

onBeforeUnmount(() => {
  switcherObserver?.disconnect()
})
</script>

<style scoped>
.billingSwitch {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.switcher {
  position: relative;
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  border-radius: 999px;
  border: 1px solid var(--light-3);
  background-color: var(--white);
}

.switcherThumb {
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 999px;
  background-color: var(--dark-5);
  pointer-events: none;
}

.switcherThumb.animated {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.switcherOption {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 24px;
  border: 0;
  border-radius: 999px;
  background-color: transparent;
  font-family: inherit;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: var(--dark-3);
  cursor: pointer;
  transition: color 0.3s ease;
}

.switcherOption.selected {
  font-weight: 600;
  color: var(--white);
}

@media (prefers-reduced-motion: reduce) {
  .switcherThumb.animated,
  .switcherOption {
    transition: none;
  }
}

.switcherOption:focus-visible {
  outline: 2px solid var(--dark-5);
  outline-offset: 2px;
}

.saveBadge {
  padding: 2px 10px;
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
  border-radius: 999px;
  color: #0E3B20;
  background-color: #6FDB8F;
}

.switcherNote {
  margin-top: 10px;
  font-size: 14px;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.switcherNote.hidden {
  visibility: hidden;
}
</style>
